/**
 * External Application Routes
 * 
 * Centralized configuration for external app navigation.
 * This prevents tight coupling between UI labels and routing logic.
 */

export enum ExternalAppKey {
  KONNECT_HOME = 'KONNECT_HOME',
  KONNECT_CORE = 'KONNECT_CORE',
  DATA_EXPLORATION = 'DATA_EXPLORATION',
  ANALYTICS = 'ANALYTICS',
  VISUALIZATION_3D = 'VISUALIZATION_3D',
  PLANNING_4D = 'PLANNING_4D',
  COST_5D = 'COST_5D',
  LAYOUT_STUDIO = 'LAYOUT_STUDIO',
  ENERGY_MODEL = 'ENERGY_MODEL',
  EDITOR_3D = 'EDITOR_3D',
}

export interface ExternalApp {
  key: ExternalAppKey;
  label: string;
  url: string;
  alt: string;
}

export const EXTERNAL_APPS: Record<ExternalAppKey, ExternalApp> = {
  [ExternalAppKey.KONNECT_HOME]: {
    key: ExternalAppKey.KONNECT_HOME,
    label: 'Konnect home',
    url: '/home',
    alt: 'Konnect home',
  },
  [ExternalAppKey.KONNECT_CORE]: {
    key: ExternalAppKey.KONNECT_CORE,
    label: 'Konnect core',
    url: '/core',
    alt: 'Konnect core',
  },
  [ExternalAppKey.DATA_EXPLORATION]: {
    key: ExternalAppKey.DATA_EXPLORATION,
    label: 'Data exploration',
    url: '/data-exploration',
    alt: 'Data exploration',
  },
  [ExternalAppKey.ANALYTICS]: {
    key: ExternalAppKey.ANALYTICS,
    label: 'Analytics',
    url: '/analytics',
    alt: 'Analytics',
  },
  [ExternalAppKey.VISUALIZATION_3D]: {
    key: ExternalAppKey.VISUALIZATION_3D,
    label: '3D Visualization',
    url: '/3d-visualization',
    alt: '3D Visualization',
  },
  [ExternalAppKey.PLANNING_4D]: {
    key: ExternalAppKey.PLANNING_4D,
    label: '4D Planning',
    url: '/4d-planning',
    alt: '4D Planning',
  },
  [ExternalAppKey.COST_5D]: {
    key: ExternalAppKey.COST_5D,
    label: '5D Cost exploration',
    url: '/5d-cost',
    alt: '5D Cost exploration',
  },
  [ExternalAppKey.LAYOUT_STUDIO]: {
    key: ExternalAppKey.LAYOUT_STUDIO,
    label: 'Layout studio',
    url: '/layout-studio',
    alt: 'Layout studio',
  },
  [ExternalAppKey.ENERGY_MODEL]: {
    key: ExternalAppKey.ENERGY_MODEL,
    label: 'Energy model',
    url: '/energy-model',
    alt: 'Energy model',
  },
  [ExternalAppKey.EDITOR_3D]: {
    key: ExternalAppKey.EDITOR_3D,
    label: '3D Editor',
    url: '/3d-editor',
    alt: '3D Editor',
  },
};

/**
 * Helper function to get URL by app key
 */
export const getExternalAppUrl = (key: ExternalAppKey): string => {
  return EXTERNAL_APPS[key].url;
};

/**
 * Helper function to get app config by label (for backward compatibility)
 * @deprecated Use ExternalAppKey instead of label strings
 */
export const getExternalAppByLabel = (label: string): ExternalApp | undefined => {
  return Object.values(EXTERNAL_APPS).find(app => app.label === label);
};
